import { defineComponent as _defineComponent } from 'vue'
import { shiftApi } from '@/api';
import ActionDialog from '@/components/dialog/ActionDialog.vue';
import i18n from '@/i18n';
import shiftActionsController, {
  ShiftActionControlEnum,
} from '@/lib/actions/ShiftActionsController';
import { Icon } from '@/lib/enum/Icon';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import {
  realtimeQuery,
  realtimeQueryInstantUpdate,
} from '@/lib/realtime/weak/realtimeFunctions';
import { WeakEntityRepositoryUpdateKind } from '@/lib/realtime/weak/WeakEntityRepository';
import { unassignShifts } from '@/lib/schedule-v2/scheduleV2Actions';
import { matchingShiftIds } from '@/lib/schedule-v2/scheduleV2Functions';
import { showError, showSuccess } from '@/plugins/ToastNotifications';
import { redirect } from '@/router/router';
import { routes } from '@/router/routes';
import store from '@/store';
import { toISOShortString } from '@/util/dateFunctions';
import { viewport } from '@/util/windowFunctions';
import AssignShiftV2 from '@/views/schedule/dialogs/AssignShiftV2.vue';
import CreateOpenShiftResponse from '@/views/schedule/dialogs/CreateOpenShiftResponse.vue';
import DeleteShiftDialog from '@/views/schedule/dialogs/DeleteShiftDialog.vue';
import DuplicateShift from '@/views/schedule/dialogs/DuplicateShift.vue';
import EditShiftV2 from '@/views/schedule/dialogs/EditShiftV2.vue';
import ShiftHistoryV2 from '@/views/schedule/dialogs/ShiftHistoryV2.vue';
import ToggleOpenShift from '@/views/schedule/dialogs/ToggleOpenShift.vue';
import WithdrawOpenShiftResponse from '@/views/schedule/dialogs/WithdrawOpenShiftResponse.vue';
import ShiftSwapCancellationDialog from '@/views/shift-swaps/ShiftSwapCancellationDialog.vue';
import ShiftSwapCreationDialog from '@/views/shift-swaps/ShiftSwapCreationDialog.vue';
import ShiftSwapReviewDialog from '@/views/shift-swaps/ShiftSwapReviewDialog.vue';
import { computed, reactive, ref, watch } from 'vue';
import {
  Employee,
  OpenShiftResponse,
  OpenShiftResponseStatusEnum,
  Shift,
  ShiftSwap,
  ShiftSwapStatusEnum,
  TimesheetEntry,
} from '../../../../api/v1';


export default /*#__PURE__*/_defineComponent({
  __name: 'ShiftActionsControlCenter',
  setup(__props) {

const controller = reactive(shiftActionsController);

const actingShift = computed<Shift | null>(
  () => controller.actingShift as Shift | null,
);

const isLoading = ref<number>(0);

const actionsHandling = ref<number>(0);

const timezone = computed<string>(() => store.getters.timezone);
const loggedInEmployee = computed<Employee>(
  () => store.getters.loggedInEmployee,
);

const actingTimesheet = ref<TimesheetEntry | null>(null);
const actingShiftSwap = ref<ShiftSwap | null>(null);

const isReplacementShift = computed<boolean>(
  () => shiftActionsController[ShiftActionControlEnum.ReplaceShift].isOpen,
);

const actingShiftMatchingIds = ref<Shift['id'][]>([]);

const actingShiftOpenShiftResponses = ref<OpenShiftResponse[]>([]);

const ASSIGN_SHIFT_DIALOG_ACTIONS = [
  ShiftActionControlEnum.ViewOpenShiftResponses,
  ShiftActionControlEnum.AssignShift,
  ShiftActionControlEnum.ReassignShift,
  ShiftActionControlEnum.ReplaceShift,
];

const showAssignShift = computed<boolean>(() =>
  ASSIGN_SHIFT_DIALOG_ACTIONS.some((action) => controller[action].isOpen),
);

const closeAssignShift = () => {
  ASSIGN_SHIFT_DIALOG_ACTIONS.forEach((action) => {
    controller[action].close(
      !shiftActionsController[ShiftActionControlEnum.ShiftHistory].isOpen,
    );
  });
};

const unassignShift = async () => {
  await unassignShifts([actingShift.value]).finally(() => {
    controller[ShiftActionControlEnum.UnassignShift].close(true);
  });
};

const TOGGLE_OPEN_SHIFT_DIALOG_ACTIONS = [
  ShiftActionControlEnum.MoveToOpen,
  ShiftActionControlEnum.MoveToUnassigned,
];

const showToggleOpenShift = computed<boolean>(() =>
  TOGGLE_OPEN_SHIFT_DIALOG_ACTIONS.some((action) => controller[action].isOpen),
);

const closeToggleOpenShift = () => {
  TOGGLE_OPEN_SHIFT_DIALOG_ACTIONS.forEach((action) => {
    controller[action].close(true);
  });
};

const publishShift = async () => {
  const shiftIds = [actingShift.value.id, ...actingShiftMatchingIds.value];
  await shiftApi
    .publishShifts({
      publishShiftsData: {
        shiftIds,
      },
    })
    .then(() => {
      showSuccess(
        shiftIds.length <= 25
          ? i18n.tc('shifts.bulkPublished', shiftIds.length)
          : i18n.tc('shifts.bulkPublishingQueued', shiftIds.length),
      );
      controller[ShiftActionControlEnum.PublishShift].close(true);
    });
};

const deleteShifts = (count: number | null = null) => {
  actionsHandling.value += 1;
  const allShiftIds = [actingShift.value.id, ...actingShiftMatchingIds.value];
  const shiftIdsToDelete =
    count && count < allShiftIds.length
      ? allShiftIds.slice(0, count)
      : allShiftIds;

  const deleteHandler =
    shiftIdsToDelete.length > 1
      ? () =>
          shiftApi.bulkDeleteShifts({
            bulkDeleteShiftsData: {
              ids: shiftIdsToDelete,
            },
          })
      : () => shiftApi.deleteShift({ id: actingShift.value.id });

  deleteHandler()
    .then(() => {
      shiftIdsToDelete.forEach((shiftId: number) => {
        realtimeQueryInstantUpdate(
          Entity.Shift,
          WeakEntityRepositoryUpdateKind.DELETE,
          shiftId,
        );
      });
      controller[ShiftActionControlEnum.DeleteShift].close(true);
    })
    .catch(() => {
      showError(
        i18n.tc('errorMessage.genericDeleting', 1, {
          entity: i18n.tc('entities.shift'),
        }),
      );
    })
    .finally(() => {
      actionsHandling.value -= 1;
    });
};

// Load required entities for shift actions
watch(
  () => actingShift.value,
  async (shift) => {
    if (!shift) {
      actingTimesheet.value = null;
      actingShiftMatchingIds.value = [];
      actingShiftSwap.value = null;
      actingShiftOpenShiftResponses.value = [];
      return;
    }

    isLoading.value += 1;
    /** Timesheets */
    const timesheetsQuery = realtimeQuery(Entity.TimesheetEntry)
      .where('shiftId', 'eq', shift.id)
      .fetch();
    actingTimesheet.value = (await timesheetsQuery.promise)[0] || null;

    /** Matching shifts */
    const shiftsQuery = realtimeQuery(Entity.Shift)
      .where('startsAt', 'eq', shift.startsAt)
      .where('endsAt', 'eq', shift.endsAt)
      .where('jobRoleId', 'eq', shift.jobRoleId)
      .where('locationId', 'eq', shift.locationId)
      .fetch();
    const similarShifts = await shiftsQuery.promise;
    actingShiftMatchingIds.value = matchingShiftIds(shift, similarShifts);

    /** Shift swaps */
    const shiftSwapQuery = realtimeQuery(Entity.ShiftSwap)
      .where('shiftId', 'eq', shift.id)
      .whereIn('status', [
        ShiftSwapStatusEnum.PendingAcceptance,
        ShiftSwapStatusEnum.PendingApproval,
      ])
      .fetch();
    actingShiftSwap.value = (await shiftSwapQuery.promise)[0] || null;

    /** Open Shift Responses */
    const openShiftResponseQuery = realtimeQuery(Entity.OpenShiftResponse)
      .where('shiftId', 'eq', shift.id)
      .where('status', 'neq', OpenShiftResponseStatusEnum.Rejected)
      .fetch();
    actingShiftOpenShiftResponses.value = await openShiftResponseQuery.promise;
    isLoading.value -= 1;
  },
  { deep: true },
);

watch(
  () => controller[ShiftActionControlEnum.ViewTimesheet].isOpen,
  (open) => {
    if (open) {
      redirect(
        routes.timesheets.route({
          employee: `${actingShift.value.employeeId}`,
          date: toISOShortString(actingShift.value.startsAt, timezone.value),
        }),
      );
      controller[ShiftActionControlEnum.ViewTimesheet].close(true);
    }
  },
);

return { __sfc: true,controller, actingShift, isLoading, actionsHandling, timezone, loggedInEmployee, actingTimesheet, actingShiftSwap, isReplacementShift, actingShiftMatchingIds, actingShiftOpenShiftResponses, ASSIGN_SHIFT_DIALOG_ACTIONS, showAssignShift, closeAssignShift, unassignShift, TOGGLE_OPEN_SHIFT_DIALOG_ACTIONS, showToggleOpenShift, closeToggleOpenShift, publishShift, deleteShifts, ActionDialog, ShiftActionControlEnum, Icon, belongsToEmployee, viewport, AssignShiftV2, CreateOpenShiftResponse, DeleteShiftDialog, DuplicateShift, EditShiftV2, ShiftHistoryV2, ToggleOpenShift, WithdrawOpenShiftResponse, ShiftSwapCancellationDialog, ShiftSwapCreationDialog, ShiftSwapReviewDialog }
}

})